import { useLocation, Navigate, Routes, Route, Link, useNavigate } from "react-router-dom";
import { ChevronLeft } from "@mui/icons-material";
import BasicLayout from "components/Layouts/BasicLayout";
import { StatusEnum } from "types/events.d";
import DetailsTab from "./DetailsTab";
import OccurrenceTab from "./OccurrenceTab";
import MediaTab from "./MediaTab";
import PromotionsTab from "./PromotionsTab";
import TicketsTab from "./TicketsTab";
import RSVPsTab from "./RsvpTab";
import AdminTab from "./AdminTab";
import LoadingOverlay from "components/LoadingOverlay";
import { EventProvider, useEvent } from "./EventContext";
import { useState } from "react";

const tabPaths = {
  details: "Details",
  occurrence: "Occurrence",
  media: "Media",
  promotions: "Promotions",
  tickets: "Tickets",
  rsvps: "RSVPs",
  admin: "Admin",
};

enum TabEnum {
  "details",
  "occurrence",
  "media",
  "promotions",
  "tickets",
  "rsvps",
  "admin",
}
const EventContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, error, saveEvent, event } = useEvent();
  const currentTab = location?.pathname?.split("/")?.pop() || "details";

  // Define tab order for navigation
  const tabOrder = Object.keys(tabPaths);

  const handleSave = (data?: Record<string, any>) => async () => {
    const result = await saveEvent(data);
    if (result) {
      navigate("/events");
    }
  };

  const getCurrentTabIndex = () => {
    return tabOrder.indexOf(currentTab) + 1;
  };

  const goToPreviousTab = () => {
    const currentIndex = tabOrder.indexOf(currentTab);
    if (currentIndex > 0) {
      navigate(tabOrder[currentIndex - 1]);
    }
  };

  const goToNextTab = () => {
    const currentIndex = tabOrder.indexOf(currentTab);
    if (currentIndex < tabOrder.length - 1) {
      navigate(tabOrder[currentIndex + 1]);
    }
  };

  return (
    <BasicLayout>
      <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4 space-y-3 sm:space-y-4">
        {/* Header section */}
        <div className="flex flex-row justify-between items-center space-y-3 sm:space-y-0">
          <div className="flex items-center">
            <button
              className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 flex-shrink-0"
              onClick={() => navigate("/events")}
              aria-label="Back to events"
            >
              <ChevronLeft />
            </button>
            <h1 className="text-xl md:text-2xl font-bold ml-2 truncate">
              {event?.title ? event.title : "Event"}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2 justify-end sm:justify-start sm:ml-4">
            <button
              className="px-3 py-1.5 md:px-4 md:py-2 bg-gray-500 text-white font-semibold text-sm md:text-base rounded hover:bg-gray-600 disabled:opacity-50"
              onClick={handleSave({ status: StatusEnum.Pending })}
              disabled={loading}
            >
              Save
            </button>
            <button
              className="px-3 py-1.5 md:px-4 md:py-2 bg-blue-500 text-white font-semibold text-sm md:text-base rounded hover:bg-blue-600 disabled:opacity-50"
              onClick={handleSave({ status: StatusEnum.Published })}
              disabled={loading}
            >
              Publish
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 sm:px-4 sm:py-3 rounded relative text-sm sm:text-base" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="w-full">
          {/* Desktop Tab Navigation */}
          <div className="hidden md:block border-b border-gray-200 mb-6">
            <nav className="flex">
              {Object.entries(tabPaths).map(([key, value]) => (
                <Link
                  key={key}
                  to={key}
                  className={`px-4 py-2 font-medium text-sm ${currentTab === key
                    ? "border-b-2 border-blue-500 text-blue-500"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  {value}
                </Link>
              ))}
            </nav>
          </div>

          {/* Mobile Tab Navigation */}
          <div className="md:hidden border-b border-gray-200 mb-4">
            <div className="flex items-center justify-between py-2">
              <button
                onClick={goToPreviousTab}
                disabled={tabOrder.indexOf(currentTab) === 0}
                className={`p-2 rounded-full ${tabOrder.indexOf(currentTab) === 0 ? 'text-gray-300' : 'text-blue-600'}`}
                aria-label="Previous tab"
              >
                ←
              </button>
              <div className="font-medium">
                {tabPaths[currentTab]}
              </div>
              <button
                onClick={goToNextTab}
                disabled={tabOrder.indexOf(currentTab) === tabOrder.length - 1}
                className={`p-2 rounded-full ${tabOrder.indexOf(currentTab) === tabOrder.length - 1 ? 'text-gray-300' : 'text-blue-600'}`}
                aria-label="Next tab"
              >
                →
              </button>
            </div>
          </div>

          <div className="pb-20 sm:pb-16 md:pb-4">
            <Routes>
              <Route path="details" element={<DetailsTab />} />
              <Route path="occurrence" element={<OccurrenceTab />} />
              <Route path="media" element={<MediaTab />} />
              <Route path="promotions" element={<PromotionsTab />} />
              <Route path="tickets" element={<TicketsTab />} />
              <Route path="rsvps" element={<RSVPsTab />} />
              <Route path="admin" element={<AdminTab />} />
              <Route path="/" element={<Navigate to="details" replace />} />
            </Routes>
          </div>
        </div>
      </div>
      <LoadingOverlay loading={loading} />
    </BasicLayout>
  );
};

const Event: React.FC = () => {
  return (
    <EventProvider>
      <EventContent />
    </EventProvider>
  );
};

export default Event;
